<template>
    <div class="wizard-forms vrm-dess-wizard__grid">
        <div class="row ">
            <div class="col-md-10 col-md-offset-1">
                <h2>{{ $t('firmware-library.files') }}</h2>
                <p>
                    {{
                        $t('firmware-library.filesDescription', {
                            i18nFallback:
                                'You can upload multiple firmware files within the supported file format: .dup, .bup, .xup, .vup, .vff, .evcsup',
                        })
                    }}
                </p>
                <vrm-scroll-fade>
                    <vrm-foldable-table :items="files" key-by="name">
                        <template v-slot:thead>
                            <tr>
                                <th>
                                    {{
                                        $t('firmware-library.firmwareFiles', {
                                            i18nFallback: 'Firmware file(s)',
                                        })
                                    }}
                                </th>
                                <th>
                                    {{
                                        $t('firmware-library.numberOfProductIds', {
                                            i18nFallback: 'Number of Product ID(s)',
                                        })
                                    }}
                                </th>
                                <th />
                            </tr>
                        </template>
                        <template v-slot:main="{ item: file, toggle, expanded }">
                            <vrm-table-cell-file
                                :text="file.name"
                                :download-url="file.downloadUrl"
                            ></vrm-table-cell-file>
                            <vrm-table-cell-text
                                :text="String(file.idProducts.length)"
                            ></vrm-table-cell-text>
                            <td class="vrm-table__actions">
                                <a
                                    class="vrm-table__icon--link"
                                    :class="
                                        expanded
                                            ? 'sg-vrm-icon_059_min-round-16px'
                                            : 'sg-vrm-icon_058_plus-round-16px'
                                    "
                                    @click="toggle"
                                />
                            </td>
                        </template>
                        <template v-slot:extra="{ item: file }">
                            <td colspan="999">
                                <vrm-foldable-table :items="file.idProducts" key-by="">
                                    <template v-slot:thead>
                                        <th>{{ $t('firmware-library.productIds') }}</th>
                                        <th>{{ $t('firmware-library.firmwareVersions') }}</th>
                                        <th />
                                    </template>
                                    <template v-slot:main="{ item: idProduct }">
                                        <vrm-table-cell-text
                                            :text="idProduct"
                                        ></vrm-table-cell-text>
                                        <vrm-table-cell-text
                                            :text="String(file.firmwareVersion)"
                                        ></vrm-table-cell-text>
                                    </template>
                                </vrm-foldable-table>

                                <div class="sg-spread"></div>
                            </td>
                        </template>
                    </vrm-foldable-table>
                </vrm-scroll-fade>
                <br />

                <h4>
                    {{
                        $t('firmware-library.minVersionsHeader', {
                            i18nFallback: 'Minimum versions and distribution channels',
                        })
                    }}
                </h4>
                <p>
                    {{
                        $t('firmware-library.minVersionsDescription', {
                            i18nFallback:
                                'The minimum version values are pre-filled based on the previous firmware release but can be adjusted as needed.',
                        })
                    }}
                </p>
                <br />

                <div class="row">
                    <div class="col-xs-12 col-sm-6 p-l-0">
                        <vrm-text-field
                            v-model="minVictronConnectVersion"
                            :required="true"
                            :title="
                                $t('firmware-library.minVictronConnectVersion', {
                                    i18nFallback: 'Minimum VictronConnect version',
                                })
                            "
                        ></vrm-text-field>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12 col-sm-6 p-l-0">
                        <vrm-text-field
                            v-model="minVersionSettings"
                            :required="true"
                            :title="
                                $t('firmware-library.minVersionSettings', {
                                    i18nFallback: 'Minimum setting required version',
                                })
                            "
                        ></vrm-text-field>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12 col-sm-6 p-l-0">
                        <vrm-text-field
                            v-model="minVersion"
                            :required="true"
                            :title="
                                $t('firmware-library.minVersion', {
                                    i18nFallback: 'Minimum firmware version',
                                })
                            "
                        ></vrm-text-field>
                    </div>
                </div>

                <div class="row">
                    <div class="col-xs-12 col-sm-6 p-l-0">
                        <label class="sg-textfield-label">
                            <span class="sg-textfield-label-caption">
                                {{
                                    $t('firmware-library.selectDistributionChannels', {
                                        i18nFallback: 'Select distribution channels',
                                    })
                                }}
                            </span>
                        </label>
                        <vrm-form-checkbox v-model="releaseChannel" class="sg-element">
                            <template v-slot:title>
                                {{
                                    $t('firmware-library.victronConnect', {
                                        i18nFallback: 'VictronConnect',
                                    })
                                }}
                            </template>
                        </vrm-form-checkbox>
                        <vrm-form-checkbox v-model="betaChannel" class="sg-element">
                            <template v-slot:title>
                                {{
                                    $t('firmware-library.victronConnectBeta', {
                                        i18nFallback: 'VictronConnect BETA',
                                    })
                                }}
                            </template>
                        </vrm-form-checkbox>
                    </div>
                </div>

                <br />
            </div>
        </div>
    </div>
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import VrmFilePickerField from '@/vue/components/forms/VrmFilePickerField.vue';
import VrmScrollFade from '@/vue/components/shared/VrmScrollFade.vue';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable.vue';
import VrmDropdown from '@/vue/components/shared/VrmDropdown.vue';
import VrmTableCellText from '@/vue/components/table/VrmTableCellText.vue';
import VrmTableCellFile from '@/vue/components/table/VrmTableCellFile.vue';
import VrmTextField from '@/vue/components/forms/VrmTextField.vue';
import {
    CREATE_NEW_RELEASE_FORM_FIELDS,
    NEW_RELEASE,
} from '@/vue/store/modules/create-new-release-form';
import VrmCheckbox from '@/vue/components/forms/VrmCheckbox.vue';
import VrmFormCheckbox from '@/vue/components/forms/VrmFormCheckbox.vue';

@Component({
    components: {
        VrmFormCheckbox,
        VrmCheckbox,
        VrmTextField,
        VrmTableCellFile,
        VrmTableCellText,
        VrmDropdown,
        VrmFoldableTable,
        VrmScrollFade,
        VrmFilePickerField,
    },
})
export default class Properties extends Vue {
    @Prop()
    initialValues;
    changedField = new Map();

    creating = false;
    error = null;

    get files() {
        return this.$store.state.createNewReleaseForm.files;
    }

    created() {
        let fields = [
            'minVictronConnectVersion',
            'minVersionSettings',
            'minVersion',
            'feedChannels',
        ];
        let initialValuesAllNull = true;
        fields.forEach(field => {
            this.changedField.set(field, false);
            if (this.initialValues[field] !== null) {
                initialValuesAllNull = false;
            }
        });
        this.creating = initialValuesAllNull;
    }

    compareAndEmitEdit(field, newValue) {
        if (this.initialValues[field] === null) {
            this.changedField.set(field, true);
        } else if (field === 'feedChannels') {
            this.changedField.set(
                field,
                this.initialValues[field].sort().join(',') === newValue.sort().join(','),
            );
        } else {
            this.changedField.set(
                field,
                newValue.toString() !== this.initialValues[field].toString(),
            );
        }
        let hasChange = false;
        this.changedField.forEach(value => {
            hasChange = hasChange || value;
        });
        if (this.creating) {
            this.$emit('creating', true);
        }
        this.$emit('change', {
            hasChange: hasChange,
            formName: 'properties',
        });
    }

    get minVictronConnectVersion() {
        return this.$store.state.createNewReleaseForm.minVictronConnectVersion;
    }

    set minVictronConnectVersion(value) {
        this.compareAndEmitEdit('minVictronConnectVersion', value);
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: value,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.minVictronConnectVersion,
        });
    }

    get minVersionSettings() {
        return this.$store.state.createNewReleaseForm.minVersionSettings;
    }

    set minVersionSettings(value) {
        this.compareAndEmitEdit('minVersionSettings', value);
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: value,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.minVersionSettings,
        });
    }

    get minVersion() {
        return this.$store.state.createNewReleaseForm.minVersion;
    }

    set minVersion(value) {
        this.compareAndEmitEdit('minVersion', value);
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: value,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.minVersion,
        });
    }

    get releaseChannel() {
        return this.$store.state.createNewReleaseForm.feedChannels.includes('release');
    }

    set releaseChannel(value) {
        let newFeedChannels = this.$store.state.createNewReleaseForm.feedChannels;
        if (this.releaseChannel && !value) {
            newFeedChannels.splice(newFeedChannels.indexOf('release'), 1);
        } else if (!this.releaseChannel && value) {
            newFeedChannels.push('release');
        }
        this.compareAndEmitEdit('feedChannels', newFeedChannels);
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: newFeedChannels,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.feedChannels,
        });
    }

    get betaChannel() {
        return this.$store.state.createNewReleaseForm.feedChannels.includes('beta');
    }

    set betaChannel(value) {
        let newFeedChannels = this.$store.state.createNewReleaseForm.feedChannels;
        if (this.betaChannel && !value) {
            newFeedChannels.splice(newFeedChannels.indexOf('beta'), 1);
        } else if (!this.betaChannel && value) {
            newFeedChannels.push('beta');
        }
        this.compareAndEmitEdit('feedChannels', newFeedChannels);
        this.$store.commit(NEW_RELEASE.SET_FORM_FIELD, {
            value: newFeedChannels,
            key: CREATE_NEW_RELEASE_FORM_FIELDS.feedChannels,
        });
    }
}
</script>
